import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import './App.css';
import './site/site.css';
// Small, and in the main CSS so prerendered guides don't change layout when their code loads.
import './site/pages/guides/guides.css';
// Home and landing pages stay in the main bundle: ad and search visitors arrive there.
import HomePage from './site/pages/HomePage';
import NotFoundPage from './site/pages/NotFoundPage';
import LandingPage from './site/pages/landing/LandingPage';
import lazyPage from './lazyPage';
import { LANDING_PAGES } from './site/pages/landing';
import { usePageTracking } from './site/analytics';
import pageMeta from './site/pageMeta.json';

// Everything else loads when visited (see lazyPage.js). Stripe and Google Maps are only imported
// from the booking flow and the account pages, so they load with those.
const AdminApp = lazyPage(() => import('./admin/AdminApp'));
const AdminLoginPage = lazyPage(() => import('./admin/LoginPage'));
const AccountRoutes = lazyPage(() => import('./site/account/AccountRoutes'));
const BookingFlow = lazyPage(() => import('./components/BookingFlow/BookingFlow'));
const AboutPage = lazyPage(() => import('./site/pages/AboutPage'));
const ContactPage = lazyPage(() => import('./site/pages/ContactPage'));
const HelpPage = lazyPage(() => import('./site/pages/HelpPage'));
const TermsPage = lazyPage(() => import('./site/pages/TermsPage'));
const PrivacyPage = lazyPage(() => import('./site/pages/PrivacyPage'));
const FleetPage = lazyPage(() => import('./site/pages/FleetPage'));
const GuidesIndexPage = lazyPage(() => import('./site/pages/guides/GuidesIndexPage'));
const GuidePage = lazyPage(() => import('./site/pages/guides/GuidePage'));

const GUIDE_PATHS = Object.keys(pageMeta.pages).filter((p) => p.startsWith('/guides/'));

// Prerendered pages that are lazy: src/index.js loads their code before hydrating.
const PRERENDERED_LAZY = {
  '/about': AboutPage,
  '/contact': ContactPage,
  '/help': HelpPage,
  '/terms': TermsPage,
  '/privacy': PrivacyPage,
  '/fleet': FleetPage,
  '/guides': GuidesIndexPage,
  ...Object.fromEntries(GUIDE_PATHS.map((p) => [p, GuidePage])),
};

export function preloadRoute(route) {
  const page = PRERENDERED_LAZY[route];
  return page ? page.preload() : Promise.resolve();
}

// Google tag page views (does nothing unless the tag IDs are configured).
function PageTracking() {
  usePageTracking();
  return null;
}

// Dev-only style guide for the website redesign; never shipped in production builds.
const DesignPreview = process.env.NODE_ENV !== 'production' ? lazy(() => import('./site/DesignPreview')) : null;

function App() {
  return (
    <Router>
      <ScrollToTop />
      <PageTracking />
      <div className="App">
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/admin-classic/*" element={<Navigate to="/admin" replace />} />
          <Route path="/dashboard/*" element={<AccountRoutes />} />
          {DesignPreview && (
            <Route path="/design" element={<Suspense fallback={null}><DesignPreview /></Suspense>} />
          )}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/book" element={<BookingFlow />} />
          <Route path="/guides" element={<GuidesIndexPage />} />
          <Route path="/guides/:slug" element={<GuidePage />} />
          {LANDING_PAGES.map((page) => (
            <Route key={page.path} path={page.path} element={<LandingPage page={page} />} />
          ))}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;

